<script lang="ts" setup>
import type { RouterOutput } from "~/plugins/trpc.client";
import { useNuxtApp } from "#app";
import { useQueryClient } from "@tanstack/vue-query";
import {
  resolveWalletInviteStatus,
  type WalletInviteStatus,
} from "./campaignDisplayHelpers";
import { successMessage, errorMessage } from "~/composables/useToast";
import { useDisconnect, useAppKitAccount } from "@reown/appkit/vue";

type Wallet = RouterOutput["listCampaignWeekWallets"][number];

const props = defineProps<{
  campaignWeekId?: string;
  wallets: Wallet[];
}>();

const emit = defineEmits(["verified", "removed"]);

const { $trpc } = useNuxtApp();
const queryClient = useQueryClient();
const { disconnect } = useDisconnect();
const account = useAppKitAccount();

const verifyOwnershipSidebarOpen = ref(false);
const verifyOwnershipWallet = ref<Wallet | null>(null);

const statusBadgeClasses: Record<WalletInviteStatus["kind"], string> = {
  eligible: "bg-[#E1F7EC] text-[#0F7A3D]",
  needsVerification: "bg-[#FCEBEB] text-[#9F1D1D]",
  awaitingEligibility: "bg-[#E9E9FF] text-[#4438CA]",
  notInvited: "bg-[#F3F4F6] text-[#6B7280]",
};

function statusFor(wallet: Wallet) {
  return resolveWalletInviteStatus({
    isEligible: wallet.isEligible,
    walletState: wallet.walletState,
    isInvited: wallet.isInvited,
  });
}

function openVerifyOwnershipSidebar(wallet: Wallet) {
  verifyOwnershipSidebarOpen.value = true;
  verifyOwnershipWallet.value = wallet;
}

async function onWalletVerified() {
  // Close the verification sidebar first
  verifyOwnershipSidebarOpen.value = false;

  if (!verifyOwnershipWallet.value) {
    return;
  }

  try {
    // Eligibility reset and check now happen automatically in the backend
    // when a wallet is verified (see creator-backend wallet.service.ts)
    console.log("Wallet verified, waiting for eligibility check...");

    // Poll for eligibility changes (up to 5 seconds)
    let attempts = 0;
    const maxAttempts = 10; // 10 attempts * 500ms = 5 seconds

    while (attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Invalidate and refetch campaign data
      await Promise.all([
        props.campaignWeekId
          ? queryClient.invalidateQueries({
              queryKey: ["listCampaignWeekWallets", props.campaignWeekId],
            })
          : Promise.resolve(),
        queryClient.invalidateQueries({
          queryKey: ["getCampaign", props.campaignWeekId],
        }),
        // Invalidate all campaign list queries (with and without params)
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey[0];
            return typeof key === "string" && key.includes("Campaign");
          },
        }),
      ]);

      // Refetch to get the latest campaign data
      const campaignData = await $trpc.getCampaign.query({
        campaignWeekId: props.campaignWeekId!,
      });

      if (!campaignData.campaignWeek) {
        console.log("No campaign week data available");
        emit("verified");
        return;
      }

      console.log(
        `Attempt ${attempts + 1}: eligibility state =`,
        campaignData.campaignWeek.eligibilityState,
      );

      // If user is now eligible, close sidebar and navigate to campaign page
      if (campaignData.campaignWeek.eligibilityState === "eligible") {
        console.log(
          "User is now eligible, closing sidebar and navigating to campaign page",
        );

        // Close the outer sidebar (wallets panel) by emitting global event
        globalEmit("campaignForSidebarClicked", {
          campaignWeekIdToOpen: undefined,
          seeCriteria: false,
        });

        // Navigate to campaign page
        await navigateTo(`/campaign/${props.campaignWeekId}`);
        return;
      }

      // If user is ineligible (not pending), stop waiting
      if (campaignData.campaignWeek.eligibilityState === "ineligible") {
        console.log("User is ineligible, keeping sidebar open");
        emit("verified");
        return;
      }

      attempts++;
    }

    console.log("Eligibility check timed out");
    emit("verified");
  } catch (error) {
    console.error("Failed to trigger eligibility re-evaluation:", error);
    // Still invalidate queries and emit verified
    await Promise.all([
      props.campaignWeekId
        ? queryClient.invalidateQueries({
            queryKey: ["listCampaignWeekWallets", props.campaignWeekId],
          })
        : Promise.resolve(),
      // Invalidate all campaign list queries
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return typeof key === "string" && key.includes("Campaign");
        },
      }),
    ]);
    emit("verified");
  }
}

async function removeWallet(wallet: Wallet) {
  if (!props.campaignWeekId) {
    return;
  }

  try {
    await $trpc.detachCampaignWeekWallet.mutate({
      campaignWeekId: props.campaignWeekId,
      walletUuid: wallet.walletUuid,
    });

    // If this wallet is currently connected in AppKit, disconnect it
    if (
      account.value.address &&
      wallet.address.toLowerCase() === account.value.address.toLowerCase()
    ) {
      console.log("Disconnecting currently connected wallet from AppKit");
      await disconnect();
    }

    // Immediately invalidate the wallet list query to refresh the UI
    await queryClient.invalidateQueries({
      queryKey: ["listCampaignWeekWallets", props.campaignWeekId],
    });

    successMessage("Wallet removed from campaign");
    emit("removed");
  } catch (error) {
    console.error(error);
    errorMessage("Failed to remove wallet. Please try again.");
  }
}

const isAbortingEligibilityCheck = ref(false);

async function abortWalletEligibilityCheck() {
  if (!props.campaignWeekId) {
    return;
  }

  try {
    isAbortingEligibilityCheck.value = true;

    await $trpc.abortWalletEligibilityCheck.mutate({
      campaignWeekId: props.campaignWeekId,
    });

    // Invalidate queries to refresh the UI
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["listCampaignWeekWallets", props.campaignWeekId],
      }),
      queryClient.invalidateQueries({
        queryKey: ["getCampaign", props.campaignWeekId],
      }),
    ]);

    successMessage(
      "Wallet eligibility check aborted. Please connect your invited wallet again.",
    );
    emit("removed");
  } catch (error) {
    console.error(error);
    errorMessage("Failed to abort eligibility check. Please try again.");
  } finally {
    isAbortingEligibilityCheck.value = false;
  }
}
</script>

<template>
  <div class="space-y-3">
    <div
      v-for="wallet in wallets"
      :key="wallet.walletUuid"
      class="flex flex-col gap-3 rounded-2xl border border-[#E6E4FF] bg-white p-4 shadow-sm"
    >
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-center gap-2 min-w-0">
          <IconsWalletIcon class="text-[#1500FF]" />
          <div class="truncate font-medium text-sm" :title="wallet.address">
            {{ wallet.address }}
          </div>
        </div>
        <span
          class="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold"
          :class="statusBadgeClasses[statusFor(wallet).kind]"
        >
          {{ statusFor(wallet).label }}
        </span>
      </div>

      <div
        class="flex flex-wrap items-center justify-between gap-3 text-xs text-black/55"
      >
        <div class="flex items-center gap-3">
          <template v-if="statusFor(wallet).kind === 'needsVerification'">
            <button
              class="text-sm font-semibold text-[#6659FF] hover:underline"
              @click="openVerifyOwnershipSidebar(wallet)"
            >
              Verify ownership
            </button>
            <button
              class="text-sm font-semibold text-[#9F1D1D] hover:underline"
              @click="removeWallet(wallet)"
            >
              Remove
            </button>
          </template>
          <template v-else-if="statusFor(wallet).kind === 'notInvited'">
            <span class="text-sm text-[#6B7280]">
              Wallet you've connected is not eligible, please try with a
              different wallet.
            </span>
            <button
              class="text-sm font-semibold text-[#9F1D1D] hover:underline"
              @click="removeWallet(wallet)"
            >
              Remove
            </button>
          </template>
          <template v-else-if="statusFor(wallet).kind === 'eligible'">
            <span class="text-[#0F7A3D] font-medium">Verified</span>
          </template>
          <template
            v-else-if="statusFor(wallet).kind === 'awaitingEligibility'"
          >
            <span class="text-black/50">Eligibility refreshing</span>
            <button
              class="text-sm font-semibold text-[#9F1D1D] hover:underline"
              :disabled="isAbortingEligibilityCheck"
              @click="abortWalletEligibilityCheck"
            >
              {{ isAbortingEligibilityCheck ? "Removing..." : "Remove" }}
            </button>
          </template>
          <span v-else class="text-black/50">Eligibility refreshing</span>
        </div>
      </div>
    </div>

    <Sidebar
      v-if="
        verifyOwnershipSidebarOpen &&
        verifyOwnershipWallet?.address &&
        verifyOwnershipWallet?.walletUuid &&
        verifyOwnershipWallet?.caipAddress
      "
      @close="verifyOwnershipSidebarOpen = false"
      ><CampaignVerifyOwnershipSidebar
        :address="verifyOwnershipWallet?.address"
        :wallet-id="verifyOwnershipWallet?.walletUuid"
        :caip-address="verifyOwnershipWallet?.caipAddress"
        @verified="onWalletVerified()"
    /></Sidebar>
  </div>
</template>
